import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  useTheme,
  useMediaQuery,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter
} from '@mui/material';
import { startOfMonth, endOfMonth, format, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PieChartIcon from '@mui/icons-material/PieChart';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { supabase } from '../lib/supabase';
import CategoryPieChart from '../components/CategoryPieChart';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Função para formatar data sem conversão de timezone
const formatDateWithoutTimezone = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

function ReportsPersonal() {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transactionType, setTransactionType] = useState('saida');
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [categoryTransactions, setCategoryTransactions] = useState([]);
  const [categoryTransactionsLoading, setCategoryTransactionsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [transactionsModalOpen, setTransactionsModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState('value');
  const [sortDirection, setSortDirection] = useState('desc');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Função para gerar os botões de meses
  const generateMonthButtons = () => {
    const today = new Date();
    const months = [];
    
    // Gerar 6 meses (atual + 5 anteriores)
    for (let i = 0; i < 6; i++) {
      const monthDate = subMonths(today, i);
      const monthName = format(monthDate, 'MMMM', { locale: ptBR });
      const monthYear = format(monthDate, 'yyyy');
      const monthKey = format(monthDate, 'yyyy-MM');
      
      months.push({
        key: monthKey,
        name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        year: monthYear,
        date: monthDate
      });
    }
    
    return months;
  };

  // Função para filtrar por mês
  const handleMonthFilter = (monthDate) => {
    const firstDay = startOfMonth(monthDate);
    const lastDay = endOfMonth(monthDate);
    setDateRange([firstDay, lastDay]);
    setSelectedMonth(monthDate);
  };

  // Função para limpar filtro de mês
  const clearMonthFilter = () => {
    setDateRange([null, null]);
    setSelectedMonth(null);
  };

  const getDateRange = () => {
    if (dateRange[0] && dateRange[1]) {
      return {
        start: dateRange[0],
        end: dateRange[1]
      };
    } else {
      // Se não há filtro de data, usar mês atual como padrão
      const today = new Date();
      return {
        start: startOfMonth(today),
        end: endOfMonth(today)
      };
    }
  };

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      const startDate = format(dateRange.start, 'yyyy-MM-dd');
      const endDate = format(dateRange.end, 'yyyy-MM-dd');

      // Primeiro, buscar todas as categorias com seus relacionamentos pai
      const { data: allCategories, error: categoriesError } = await supabase
        .from('personal_categories')
        .select(`
          id,
          name,
          parent_id,
          parent:parent_id (id, name)
        `);

      if (categoriesError) throw categoriesError;

      // Criar um mapeamento de categoria_id para categoria pai
      const categoryParentMap = {};
      allCategories?.forEach(category => {
        if (category.parent_id) {
          // É uma subcategoria, mapear para a categoria pai
          categoryParentMap[category.id] = category.parent?.name || category.name;
        } else {
          // É uma categoria principal, mapear para si mesma
          categoryParentMap[category.id] = category.name;
        }
      });

      // Buscar transações
      let query = supabase
        .from('personal_transactions')
        .select(`
          value,
          category_id,
          category:personal_categories(name, id, parent_id)
        `)
        .eq('type', transactionType)
        .is('deleted_at', null)
        .gte('date', startDate)
        .lte('date', endDate);

      const { data: transactions, error } = await query;

      if (error) throw error;

      // Agrupar por categoria principal (agrupando subcategorias)
      const categoryTotals = {};
      
      transactions.forEach(transaction => {
        let categoryName = 'Sem Categoria';
        
        if (transaction.category_id) {
          // Usar o mapeamento para obter o nome da categoria principal
          categoryName = categoryParentMap[transaction.category_id] || transaction.category?.name || 'Sem Categoria';
        }
        
        const value = Math.abs(parseFloat(transaction.value));
        
        if (!categoryTotals[categoryName]) {
          categoryTotals[categoryName] = {
            name: categoryName,
            value: 0,
            count: 0
          };
        }
        
        categoryTotals[categoryName].value += value;
        categoryTotals[categoryName].count += 1;
      });

      // Converter para array e ordenar por valor
      const chartDataArray = Object.values(categoryTotals)
        .sort((a, b) => b.value - a.value)
        .map((item, index) => ({
          ...item,
          id: index,
          percentage: 0 // Será calculado depois
        }));

      // Calcular porcentagens
      const totalValue = chartDataArray.reduce((sum, item) => sum + item.value, 0);
      const chartDataWithPercentages = chartDataArray.map(item => ({
        ...item,
        percentage: totalValue > 0 ? (item.value / totalValue) * 100 : 0
      }));

      setChartData(chartDataWithPercentages);
    } catch (error) {
      console.error('Erro ao buscar dados do gráfico:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [dateRange, transactionType]);

  const handleTransactionTypeChange = (event, newValue) => {
    setTransactionType(newValue);
  };

  const getPeriodLabel = () => {
    const dateRange = getDateRange();
    const startFormatted = format(dateRange.start, 'dd/MM/yyyy');
    const endFormatted = format(dateRange.end, 'dd/MM/yyyy');
    
    return `${startFormatted} - ${endFormatted}`;
  };

  // Função para lidar com clique na fatia do gráfico
  const handleSliceClick = async (data) => {
    if (!data || !data.name) return;
    
    setSelectedCategory(data.name);
    setCategoryTransactionsLoading(true);
    setTransactionsModalOpen(true);

    try {
      const dateRange = getDateRange();
      const startDate = format(dateRange.start, 'yyyy-MM-dd');
      const endDate = format(dateRange.end, 'yyyy-MM-dd');

      // Primeiro, buscar todas as categorias com seus relacionamentos pai
      const { data: allCategories } = await supabase
        .from('personal_categories')
        .select(`
          id,
          name,
          parent_id,
          parent:parent_id (id, name)
        `);

      // Criar mapeamento de categorias
      const categoryMap = {};
      allCategories?.forEach(category => {
        categoryMap[category.name] = category.id;
        if (category.parent) {
          categoryMap[category.parent.name] = category.parent.id;
        }
      });

      // Buscar ID da categoria selecionada
      const categoryId = categoryMap[data.name];

      if (!categoryId) {
        console.warn('Categoria não encontrada:', data.name);
        setCategoryTransactions([]);
        return;
      }

      // Buscar transações da categoria (incluindo subcategorias)
      let query = supabase
        .from('personal_transactions')
        .select(`
          *,
          category:personal_categories(name, id, parent_id),
          payment_source:payment_sources(name)
        `)
        .eq('type', transactionType)
        .is('deleted_at', null)
        .gte('date', startDate)
        .lte('date', endDate);

      // Filtrar por categoria principal e suas subcategorias
      const { data: transactions, error } = await query;

      if (error) throw error;

      // Filtrar transações que pertencem à categoria selecionada ou suas subcategorias
      const filteredTransactions = transactions.filter(transaction => {
        if (!transaction.category) return false;
        
        // Se a transação pertence à categoria principal
        if (transaction.category.name === data.name) return true;
        
        // Se a transação pertence a uma subcategoria da categoria principal
        if (transaction.category.parent_id === categoryId) return true;
        
        return false;
      });

      setCategoryTransactions(filteredTransactions);
    } catch (error) {
      console.error('Erro ao buscar transações da categoria:', error);
      setCategoryTransactions([]);
    } finally {
      setCategoryTransactionsLoading(false);
    }
  };

  const handleCloseTransactionsModal = () => {
    setTransactionsModalOpen(false);
    setSelectedCategory(null);
    setCategoryTransactions([]);
    // Resetar ordenação ao fechar modal
    setSortBy('value');
    setSortDirection('desc');
  };

  // Função para lidar com ordenação
  const handleSort = (column) => {
    if (sortBy === column) {
      // Alternar direção se clicar na mesma coluna
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Mudar para nova coluna com ordenação decrescente por padrão
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  // Função para ordenar transações
  const getSortedTransactions = () => {
    if (!categoryTransactions.length) return [];

    return [...categoryTransactions].sort((a, b) => {
      let aValue, bValue;

      if (sortBy === 'value') {
        // Ordenar por valor absoluto
        aValue = Math.abs(parseFloat(a.value));
        bValue = Math.abs(parseFloat(b.value));
      } else if (sortBy === 'date') {
        // Ordenar por data
        aValue = new Date(a.date);
        bValue = new Date(b.date);
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Função para obter ícone de ordenação
  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <PieChartIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Relatórios Pessoais
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Visualize a distribuição das suas finanças por categoria
        </Typography>
      </Box>

      {/* Botões de Meses */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ mr: 2, color: 'text.secondary' }}>
            Filtrar por mês:
          </Typography>
          {generateMonthButtons().map((month) => (
            <Button
              key={month.key}
              variant={selectedMonth && format(selectedMonth, 'yyyy-MM') === month.key ? "contained" : "outlined"}
              size="small"
              onClick={() => handleMonthFilter(month.date)}
              sx={{ 
                minWidth: 'auto',
                px: 2,
                backgroundColor: selectedMonth && format(selectedMonth, 'yyyy-MM') === month.key ? 'primary.main' : 'transparent',
                '&:hover': {
                  backgroundColor: selectedMonth && format(selectedMonth, 'yyyy-MM') === month.key ? 'primary.dark' : 'grey.100'
                }
              }}
            >
              {month.name}
            </Button>
          ))}
          {selectedMonth && (
            <Button
              variant="text"
              size="small"
              onClick={clearMonthFilter}
              sx={{ 
                color: 'text.secondary',
                ml: 'auto',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Limpar filtro
            </Button>
          )}
        </Box>
      </Paper>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Data Inicial"
                value={dateRange[0]}
                onChange={(newValue) => setDateRange([newValue, dateRange[1]])}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Data Final"
                value={dateRange[1]}
                onChange={(newValue) => setDateRange([dateRange[0], newValue])}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 1 }}>
              <Tabs
                value={transactionType}
                onChange={handleTransactionTypeChange}
                centered
                variant="fullWidth"
              >
                <Tab value="saida" label="Despesas" />
                <Tab value="entrada" label="Receitas" />
              </Tabs>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Gráfico e Estatísticas */}
      <Grid container spacing={3}>
        {/* Gráfico de Pizza */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Distribuição por Categoria
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total: {formatCurrency(totalValue)}
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            ) : chartData.length > 0 ? (
              <CategoryPieChart data={chartData} onSliceClick={handleSliceClick} />
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <PieChartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhum dado encontrado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Não há transações no período selecionado
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Estatísticas */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Estatísticas
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : chartData.length > 0 ? (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total de Categorias
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {chartData.length}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Valor Total
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totalValue)}
                  </Typography>
                </Box>

                {/* Top 3 Categorias */}
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Top 3 Categorias
                  </Typography>
                  {chartData.slice(0, 3).map((item, index) => (
                    <Box 
                      key={item.id}
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        py: 1,
                        borderBottom: index < 2 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="body2">
                        {item.name}
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCurrency(item.value)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.percentage.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center">
                Nenhuma estatística disponível
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela Detalhada (apenas desktop) */}
      {!isMobile && chartData.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Detalhamento por Categoria
          </Typography>
          <Grid container spacing={2}>
            {chartData.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2,
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {item.name}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(item.value)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.count} transação{item.count !== 1 ? 's' : ''}
                    </Typography>
                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
                      {item.percentage.toFixed(1)}%
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Modal de Transações da Categoria */}
      <Dialog
        open={transactionsModalOpen}
        onClose={handleCloseTransactionsModal}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Transações - {selectedCategory}
            </Typography>
            <Button
              onClick={handleCloseTransactionsModal}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <CloseIcon />
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Período: {getPeriodLabel()}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {categoryTransactionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : categoryTransactions.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleSort('date')}
                    >
                      Data {getSortIcon('date')}
                    </TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell 
                      align="right"
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => handleSort('value')}
                    >
                      Valor {getSortIcon('value')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedTransactions().map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {formatDateWithoutTimezone(transaction.date)}
                      </TableCell>
                      <TableCell>
                        {transaction.description}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.category?.name || 'Sem categoria'} 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {transaction.payment_source?.name && (
                          <Chip 
                            label={transaction.payment_source.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          color={transaction.type === 'entrada' ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(Math.abs(transaction.value))}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4} align="right">
                      <Typography variant="subtitle1">Total:</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="subtitle1"
                        color={
                          categoryTransactions.reduce((sum, t) => sum + parseFloat(t.value), 0) >= 0
                            ? 'success.main'
                            : 'error.main'
                        }
                      >
                        {formatCurrency(
                          categoryTransactions.reduce((sum, t) => sum + parseFloat(t.value), 0)
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhuma transação encontrada
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Não há transações para a categoria "{selectedCategory}" no período selecionado
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransactionsModal}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ReportsPersonal;
